import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * UnsavedChangesDialog
 * 
 * A reusable confirmation dialog for when users try to navigate away
 * from a form with unsaved changes.
 * 
 * Usage:
 * ```
 * {showDialog && (
 *   <UnsavedChangesDialog
 *     darkMode={darkMode}
 *     onStay={() => setShowDialog(false)}
 *     onLeave={() => navigateAway()}
 *     title="Unsaved Changes"
 *     message="You have unsaved changes. Are you sure you want to leave?"
 *   />
 * )}
 * ```
 */
function UnsavedChangesDialog({
  darkMode = false,
  onStay,
  onLeave,
  title = "Unsaved Changes",
  message = "You have unsaved changes that will be lost if you leave this page.",
  stayButtonText = "Stay & Continue Editing",
  leaveButtonText = "Leave Without Saving",
  showIcon = true
}) {
  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="unsaved-changes-dialog"
      onClick={(e) => {
        // Close on backdrop click
        if (e.target === e.currentTarget) {
          onStay?.();
        }
      }}
    >
      <div 
        className={`max-w-md w-full rounded-xl shadow-2xl overflow-hidden ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3">
            {showIcon && (
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                darkMode ? 'bg-amber-900/50' : 'bg-amber-100'
              }`}>
                <AlertTriangle className={`w-5 h-5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
              </div>
            )}
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {title}
            </h3>
          </div>
        </div>
        
        {/* Content */}
        <div className="px-6 py-4">
          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {message}
          </p>
        </div>
        
        {/* Actions */}
        <div className={`px-6 py-4 flex flex-col sm:flex-row gap-3 border-t ${
          darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50'
        }`}>
          <button
            onClick={onStay}
            data-testid="unsaved-changes-stay"
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
              darkMode
                ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            }`}
          >
            {stayButtonText}
          </button>
          <button
            onClick={onLeave}
            data-testid="unsaved-changes-leave"
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
              darkMode
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {leaveButtonText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UnsavedChangesDialog;
